import { MarkSpec, NodeSpec, Schema } from 'prosemirror-model';
import { EditorState, Plugin, Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { Subject } from 'rxjs';

import { UnionToIntersection } from '@recruitee/common';
import { IconName } from '@recruitee/user-interface';

export type ProsemirrorDispatch = (tr: Transaction) => void;
export type ProsemirrorCommand = (state: EditorState, dispatch?: ProsemirrorDispatch) => boolean;

export enum EditorMode {
  View,
  Wysiwyg,
}

export abstract class EditorEvent {
  protected constructor(public origin: string, public type: string) {}
}

export interface EditorButton {
  icon: IconName;
  name?: string;
  run: (
    state: EditorState,
    dispatch: (tr: Transaction) => void,
    origin: HTMLElement,
    isContextMenu: boolean,
  ) => void;
  isActive: (state: EditorState) => boolean;
}

// Default priority is 50
export type PluginWithPriority = { priority: number; plugin: Plugin };

export interface RtPMPlugin {
  eventStream$?: Subject<any>;
  init?: (mode: EditorMode) => void;
  getNativeViewPlugins?: (mode: EditorMode) => Plugin[];
  destroy?: () => void;

  // Editor api available in editor mode
  buttons?: Record<string, EditorButton>;
  api?: Record<string, any>;
  beforeEditorInit?: <S extends Schema>(schema: S) => void;
  afterEditorInit?: <EV extends EditorView>(editorView: EV) => void;
  getNativeEditorPlugins?: <S extends Schema>(schema: S) => (PluginWithPriority | Plugin)[];
}

export type RtSchemaType<
  N extends { new (): NodeSpec; nodeName: string } = any,
  M extends { new (): MarkSpec; markName: string } = any
> = Schema<N['nodeName'], M['markName']>;

export interface IProsemirrorConfig {
  getSchema: () => Schema;
  getRenderPlugins: () => RtPMPlugin[];
  getEditorPlugins: () => RtPMPlugin[];
}

export type PluginsAPI<T extends RtPMPlugin> = UnionToIntersection<
  T extends Pick<Required<RtPMPlugin>, 'api'> ? T['api'] : never
>;
export type PluginsButtons<T extends RtPMPlugin> = UnionToIntersection<
  T extends Pick<Required<RtPMPlugin>, 'buttons'> ? T['buttons'] : never
>;

export type ConfigPlugins<T extends IProsemirrorConfig> = ReturnType<
  T['getEditorPlugins']
> extends (infer I)[]
  ? I
  : never;
export type PluginsEvents<T extends RtPMPlugin> = T extends Required<
  Pick<RtPMPlugin, 'eventStream$'>
>
  ? T['eventStream$'] extends Subject<infer I>
    ? I
    : never
  : never;

export interface OverlayPosition {
  top: number;
  left: number;
  right?: number;
  bottom?: number;
}

interface MarkLink {
  type: 'link';
  attrs: {
    href: string;
    title: string | null;
    target: '_blank';
  };
}

interface MarkStrong {
  type: 'strong';
}

interface MarkItalic {
  type: 'em';
}

interface Text {
  type: 'text';
  text: string;
  marks?: (MarkLink | MarkItalic | MarkStrong)[];
}

interface Paragraph {
  type: 'paragraph';
  content?: Text[];
}

interface Doc {
  type: 'doc';
  content: Paragraph[];
}

interface Selection {
  type: 'text' | 'all';
  anchor: number;
  head: number;
}

export interface EditorJSONState {
  doc: Doc;
  selection?: Selection;
}
